import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import TextField from '../../../../../../components/fields/FormTextField'
import { Autocomplete, Box, Grid, MenuItem } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { champsSignataireEnum } from '../enums/champsSignataireEnum'
import { fontFamilyMonserrat, gecinaBlueMain, gecinaBrownMain } from '../../../../../../theme'
import Button from '../../../../../../components/button/Button'
import { buttonTypesEnum } from '../../../../../../components/button/enums/buttonTypesEnum'
import { SignataireRechercheModel, SignataireValidateurModel } from '../../../../types/CreationProcedureModel'
import { CreationProcedureContext } from '../../../../CreationProcedure'
import FormSelectField from '../../../../../../components/fields/FormSelectField'
import { methodeAuthentification, positionSignature, typeSignataire } from '../../types/popinAjoutInterneExterneModel'
import { INITIAL_VALEURS_FORMULAIRE } from '../types/ajoutSignataireModel'
import SvgRecherche from '../../../../../../icons/SvgRecherche'
import { listeContactYousign } from '../../../../api/creationProcadureApi'
import CustomCheckbox from '../../../../../../components/checkbox/CustomCheckbox'

const FormSignataireExterne = () => {
	const methods = useForm<SignataireRechercheModel>({
		defaultValues: INITIAL_VALEURS_FORMULAIRE
	})
	const { register, handleSubmit, reset, formState: { errors }, watch, getValues, setValue, setError, clearErrors } = methods
	const { setFormValues, formValues } = useContext(CreationProcedureContext)

	const onSubmit: SubmitHandler<SignataireValidateurModel> = (values: SignataireValidateurModel) => {
		if (values[champsSignataireEnum.type] === typeSignataire.Signataire) {
			setFormValues({ ...formValues, signersValidators: [...formValues.signersValidators, { ...values }] })
		} else {
			setFormValues({
				...formValues,
				signersValidators: [
					...formValues.signersValidators.filter((signersVal) => signersVal[champsSignataireEnum.type] === typeSignataire.Validateur),
					{ ...values },
					...formValues.signersValidators.filter((signersVal) => signersVal[champsSignataireEnum.type] !== typeSignataire.Validateur)]
			})
		}
		reset(INITIAL_VALEURS_FORMULAIRE)
	}


	// Liste des utilisateurs affiché dans l'Autocomplete
	const [contactYousign, setContactYousign] = useState<SignataireValidateurModel[]>([])
	// Utilisateur selectionné, permet de savoir s'il faut afficher les champs pré-rempli
	const [utilisateurChoisi, setUtilisateurChoisi] = useState<boolean>(false)
	// Etat d'ouverture de l'autocomplete de selection de signataire
	const [autocompleteOuvert, setAutocompleteOuvert] = useState<boolean>(false)
	const [destinataireInexistant, setDestinataireInexistant] = useState<boolean>(false)

	const rechercheValue = watch(champsSignataireEnum.recherche)

	// Déclenchement explicite de la recherche (clic bouton, clic loupe ou touche Entrée)
	const recuperationUtilisateur = () => {
		if (getValues(champsSignataireEnum.recherche).length < 3) {
			setError(champsSignataireEnum.recherche, {
				type: 'minLength',
				message: 'Veuillez saisir au moins 3 caractères pour lancer la recherche'
			})
			return
		}
		clearErrors(champsSignataireEnum.recherche)
		listeContactYousign(getValues(champsSignataireEnum.recherche))
			.then((response) => {
				setContactYousign(response)
				setAutocompleteOuvert(true)
			})
			.catch((error) => {
				console.warn('Erreur lors de la recherche de contacts Yousign', error)
			})
	}

	// Reset auto quand la saisie repasse sous 3 caractères (sans appel API)
	// et effacement du message d'erreur dès que la saisie est suffisante
	useEffect(() => {
		if (rechercheValue.length < 3) {
			setContactYousign([])
			setUtilisateurChoisi(false)
			setAutocompleteOuvert(false)
			setValue(champsSignataireEnum.id, '')
			setValue(champsSignataireEnum.nom, '')
			setValue(champsSignataireEnum.prenom, '')
			setValue(champsSignataireEnum.telephone, '')
			setValue(champsSignataireEnum.courriel, '')
		} else {
			clearErrors(champsSignataireEnum.recherche)
		}
	}, [rechercheValue, setValue, clearErrors])

	return (
		<FormProvider {...methods}>
			<form
				onSubmit={handleSubmit(onSubmit)}
			>
				<Grid container spacing={2} sx={{ mb: '20px' }}>
					<Grid item xs>
						<Autocomplete
							id={champsSignataireEnum.recherche}
							freeSolo
							open={autocompleteOuvert}
							onClose={() => setAutocompleteOuvert(false)}
							sx={{ position: 'relative' }}
							// @ts-ignore
							onChange={(e, obj: SignataireValidateurModel) => {
								clearErrors()
								setValue(champsSignataireEnum.recherche, `${obj[champsSignataireEnum.nom]} ${obj[champsSignataireEnum.prenom]}` ?? '')
								setValue(champsSignataireEnum.id, obj[champsSignataireEnum.id] ?? '')
								setValue(champsSignataireEnum.nom, obj[champsSignataireEnum.nom] ?? '')
								setValue(champsSignataireEnum.prenom, obj[champsSignataireEnum.prenom] ?? '')
								setValue(champsSignataireEnum.telephone, obj[champsSignataireEnum.telephone] ?? '')
								setValue(champsSignataireEnum.courriel, obj[champsSignataireEnum.courriel] ?? '')
								setUtilisateurChoisi(true)
								setAutocompleteOuvert(false)
							}}
							options={contactYousign}
							// @ts-ignore
							getOptionLabel={(option) => ('nom' in option && 'prenom' in option && `${option[champsSignataireEnum.nom]} ${option[champsSignataireEnum.prenom]}`) || ''}
							renderInput={(params) => {
								const muiOnKeyDown = params.inputProps.onKeyDown as React.KeyboardEventHandler<HTMLInputElement> | undefined
								return (
									<div ref={params.InputProps.ref}>
										<TextField
											register={register(champsSignataireEnum.recherche)}
											{...params}
											inputProps={{
												...params.inputProps,
												onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
												// Quand l'autocomplete est ouvert, on laisse MUI gérer la nav clavier (flèches + Entrée pour valider la suggestion surlignée)
													if (event.key === 'Enter' && !autocompleteOuvert) {
														event.preventDefault()
														recuperationUtilisateur()
														return
													}
													muiOnKeyDown?.(event)
												},
												value: watch(champsSignataireEnum.recherche),
												onChange: () => {
												},
												disabled: destinataireInexistant
											}}
											placeholder="Rechercher un destinataire externe"
											sx={{
												height: 51,
												width: 345,
												border: `1px solid ${gecinaBlueMain}`,
												opacity: `${destinataireInexistant ? '0.65' : '1'}`
											}}
										/>
										<button
											type="button"
											aria-label="Rechercher un destinataire"
											onClick={() => recuperationUtilisateur()}
											disabled={destinataireInexistant}
											style={{
												cursor: destinataireInexistant ? 'default' : 'pointer',
												top: '20px',
												left: '300px',
												position: 'absolute',
												background: 'none',
												border: 'none',
												padding: 0
											}}
										>
											<SvgRecherche color={gecinaBlueMain} />
										</button>
									</div>
								)
							}}
						/>
					</Grid>
					<Grid item sx={{ maxWidth: 'max-content' }}>
						<Button
							sx={{
								height: '51px',
								minWidth: '140px',
								fontWeight: 600
							}}
							bgcolor={gecinaBlueMain}
							color={gecinaBrownMain}
							border={false}
							type={buttonTypesEnum.button}
							onClick={() => recuperationUtilisateur()}
							disabled={destinataireInexistant}
						>
							Rechercher
						</Button>
					</Grid>
					<Box
						sx={{
							width: '100%',
							paddingRight: '15px',
							paddingLeft: '16px',
							fontSize: '16px',
							marginLeft: '0px',
							boxSizing: 'border-box',
							textAlign: 'left',
							fontFamily: 'Montserrat, sans-serif',
							fontWeight: '600',
							color: gecinaBlueMain,
							mt: '30px'
						}}
					>
						Destinataire inexistant
						<CustomCheckbox
							sx={{ ml: '15px' }}
							size="medium"
							checked={destinataireInexistant}
							onChange={(event) => {
								reset(INITIAL_VALEURS_FORMULAIRE)
								setUtilisateurChoisi(false)
								setDestinataireInexistant((prevState) => !prevState)
							}}
						/>
					</Box>
				</Grid>

				{(destinataireInexistant || utilisateurChoisi) &&
					<>
						<Grid container spacing={2} sx={{ mb: '20px' }}>
							<Grid item xs>
								<TextField
									inputProps={{
										disabled: utilisateurChoisi
									}}
									sx={{
										height: 51,
										width: 345,
										border: `1px solid ${gecinaBlueMain}`,
										fontFamily: `${fontFamilyMonserrat} !important`,
										fontWeight: 500,
										fontSize: 13,
										opacity: `${utilisateurChoisi ? '0.65' : '1'}`
									}}
									register={register(champsSignataireEnum.nom, {
										required: 'Champ requis',
										maxLength: {
											value: 50,
											message: 'Le champs ne peut pas dépasser les 50 caractères'
										}
									})}
									errors={errors}
									id={champsSignataireEnum.nom}
									label="Nom*"
									placeholder="Saisir le nom du signataire"
								/>
							</Grid>
							<Grid item xs>
								<TextField
									inputProps={{
										disabled: utilisateurChoisi
									}}
									sx={{
										height: 51,
										width: 345,
										border: `1px solid ${gecinaBlueMain} !important`,
										fontFamily: `${fontFamilyMonserrat} !important`,
										fontWeight: 500,
										fontSize: 13,
										opacity: `${utilisateurChoisi ? '0.65' : '1'}`
									}}
									register={register(champsSignataireEnum.prenom, {
										required: 'Champ requis',
										maxLength: {
											value: 50,
											message: 'Le champs ne peut pas dépasser les 50 caractères'
										}
									})}
									errors={errors}
									id={champsSignataireEnum.prenom}
									label="Prénom*"
									placeholder="Saisir le prénom du signataire"
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} sx={{ mb: '20px' }}>
							<Grid item xs>
								<TextField
									inputProps={{
										disabled: utilisateurChoisi
									}}
									sx={{
										height: 51,
										width: 345,
										border: `1px solid ${gecinaBlueMain} !important`,
										fontFamily: `${fontFamilyMonserrat} !important`,
										fontWeight: 500,
										fontSize: 13,
										opacity: `${utilisateurChoisi ? '0.65' : '1'}`
									}}
									register={register(champsSignataireEnum.telephone, {
										required: 'Champ requis',
										maxLength: {
											value: 18,
											message: 'Le champs ne peut pas dépasser les 18 caractères'
										}
									})}
									errors={errors}
									id={champsSignataireEnum.telephone}
									label="Téléphone mobile*"
									placeholder="Saisir le téléphone au format +33"
								/>
								<Box
									sx={{
										width: '100%',
										paddingRight: '15px',
										paddingTop: '7px',
										fontSize: '14px',
										marginBottom: '5px',
										marginLeft: '0px',
										boxSizing: 'border-box',
										textAlign: 'left',
										fontFamily: 'Montserrat, sans-serif',
										fontWeight: '500',
										opacity: `${utilisateurChoisi ? '0.65' : '1'}`
									}}
								>
									*Indiquez l'indicatif du téléphone (ex: +33)
								</Box>
							</Grid>
							<Grid item xs>
								<TextField
									inputProps={{
										disabled: utilisateurChoisi
									}}
									sx={{
										height: 51,
										width: 345,
										border: `1px solid ${gecinaBlueMain} !important`,
										fontFamily: `${fontFamilyMonserrat} !important`,
										fontWeight: 500,
										fontSize: 13,
										opacity: `${utilisateurChoisi ? '0.65' : '1'}`
									}}
									register={register(champsSignataireEnum.courriel, {
										required: 'Champ requis',
										pattern: {
											value: /^(.+)@(.+)$/,
											message: 'Le courriel ne respecte pas le format'
										}
									})}
									errors={errors}
									id={champsSignataireEnum.courriel}
									label="Courriel*"
									placeholder="Saisir le courriel du signataire"
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} sx={{ mb: '20px' }}>
							<Grid item xs={6}>
								<FormSelectField
									register={register(champsSignataireEnum.type)}
									id={champsSignataireEnum.type}
									label="Type"
									placeholder="Type de signataire"
									sx={{
										height: 51,
										width: 345,
										border: `1px solid ${gecinaBlueMain} !important`
									}}
								>
									{
										Object.keys(typeSignataire).map(
											(type) => (
												<MenuItem value={type} key={type}>
													{typeSignataire[type as keyof typeof typeSignataire]}
												</MenuItem>
											)
										)
									}
								</FormSelectField>
							</Grid>
							{watch(champsSignataireEnum.type) === typeSignataire.Signataire &&
								(
									<>
										<Grid item xs={6}>
											<FormSelectField
												register={register(champsSignataireEnum.methodeAuth)}
												id={champsSignataireEnum.methodeAuth}
												label="Méthode d'authentification"
												placeholder="Méthode d'authentification"
												sx={{
													height: 51,
													width: 345,
													border: `1px solid ${gecinaBlueMain} !important`
												}}
											>
												{
													Object.keys(methodeAuthentification).map(
														(methode) => (
															<MenuItem value={methodeAuthentification[methode as keyof typeof methodeAuthentification]} key={methodeAuthentification[methode as keyof typeof methodeAuthentification]}>
																{methode}
															</MenuItem>
														)
													)
												}
											</FormSelectField>
										</Grid>
										<Grid item xs={6}>
											<FormSelectField
												register={register(champsSignataireEnum.positionSignature)}
												id={champsSignataireEnum.positionSignature}
												label="Position de la signature"
												placeholder="Position de la signature"
												sx={{
													height: 51,
													width: 345,
													border: `1px solid ${gecinaBlueMain} !important`
												}}
											>
												{
													Object.keys(positionSignature).map(
														(position) => (
															<MenuItem value={position} key={position}>
																{positionSignature[position as keyof typeof positionSignature]}
															</MenuItem>
														)
													)
												}
											</FormSelectField>
										</Grid>
									</>
								)
							}

							{!utilisateurChoisi &&
								<Grid item xs={6}>
									<Box
										sx={{
											width: '100%',
											paddingRight: '15px',
											fontSize: '16px',
											marginLeft: '0px',
											boxSizing: 'border-box',
											textAlign: 'left',
											fontFamily: 'Montserrat, sans-serif',
											fontWeight: '600',
											color: gecinaBlueMain,
											mt: '30px'
										}}
									>
										Enregistrer le destinataire
										<CustomCheckbox
											sx={{ ml: '15px' }}
											size="medium"
											checked={watch(champsSignataireEnum.enregistrerDestinataire)}
											register={register(champsSignataireEnum.enregistrerDestinataire)}
											onChange={(event) => {
												// @ts-ignore TODO ts-ignore
												setValue(champsSignataireEnum.enregistrerDestinataire, event.target.checked)
											}}
										/>
									</Box>
								</Grid>
							}
						</Grid>

					</>
				}
				<Grid container item xs justifyContent="flex-end" sx={{ mt: '10px', ml: 0 }} spacing={2}>
					<Grid item sx={{ maxWidth: 'max-content', ml: '24px', minWidth: 'fit-content' }}>
						<Button
							sx={{
								height: '51px',
								p: 0,
								width: '195px',
								fontWeight: 600,
								lineHeight: '18.29px'
							}}
							bgcolor={gecinaBlueMain}
							color={gecinaBrownMain}
							border={false}
							disabled={!destinataireInexistant && !utilisateurChoisi}
							type={buttonTypesEnum.submit}
						>
							Ajouter
						</Button>
					</Grid>
				</Grid>
			</form>
		</FormProvider>
	)
}

export default FormSignataireExterne